import { useMemo, useState, useCallback, useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelect from 'components/Inputs/MultiSelect';
import { getSelectedValueLink } from 'components/Inputs/MultiSelect/customize';
import ApplySelectButton from 'components/Inputs/Select/ApplyButton';
import ClearSelectButton from 'components/Inputs/Select/ClearButton';
import IconAdornment from 'components/Inputs/Adornments/IconAdornment';
import {
  useRelationshipSelect,
  useSelectEnter,
  useSelectTypeaheadWithScroll,
} from 'components/Inputs/Select/hooks';
import { TopMenuButton } from 'components/Kizen/Menu';
import CreateEntityModalShort from 'components/Modals/CreateEntity/ShortForm';
import { useEntityCreationModal } from 'components/Modals/CreateEntity/hook';
import { useRelationshipActionOverride } from 'components/Fields/hooks/useRelationshipActionOverride';
import CreateEntityModalFull from 'components/Modals/CreateEntity/FullForm';
import { useModalControl } from 'hooks/useModalControl';
import { FIELD_TYPES } from 'utility/constants';
import FieldService from 'services/FieldService';
import { RelationshipConfirmationModal } from 'components/Modals/RelationshipConfirmationModal';
import { useCustomSelectMenuProps } from 'hooks/keyboardEventHandler/useCustomSelectMenuProps';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useQueryClient } from '@tanstack/react-query';
import { RELATION_FIELD } from 'queries/query-keys';
import useField from 'hooks/useField';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { SELECTION_SCOPE } from 'constants/fields';
import { getUserLabel } from 'utility/TransformToSelectOptions';

const RelationshipMany = forwardRef(
  (
    {
      fieldId,
      field,
      entityId,
      label,
      value: valueProp = EMPTY_ARRAY,
      onChange,
      className = '',
      disabled,
      alwaysOpen = false,
      inModal = false,
      categorizedFields = EMPTY_ARRAY,
      fieldType,
      onFocus,
      onMenuOpen,
      onMenuClose,
      hideAddButton,
      keepPreviousData = true,
      excludeExistingValues = false,
      shouldRefetchOnMount = false,
      checkConflictingPrimaryRelationship = false,
      portal,
      setConflicts,
      ...others
    },
    ref
  ) => {
    const { assignFieldHandle } = useKeyBoardContext();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showEntityCreateModal, setShowEntityCreateModal] = useState(false);
    const selectRef = useRef(null);

    const [value, setValue] = useField(valueProp);

    const isContact = field.relation?.fetchUrl === 'client';

    const {
      initialValues,
      setInitialValues,
      parseTypeAhead,
      objectType,
      hasAbilityToAdd,
    } = useEntityCreationModal(field);

    const { executeOverride } = useRelationshipActionOverride(
      field.relation?.relatedObject
    );

    const [{ displayValue, handleChange, relationship }] =
      useRelationshipSelect({
        value,
        onChange,
        field,
        isMulti: true,
      });

    const chosenValueIds = useMemo(
      () => (value || []).map((item) => item.value ?? item.id),
      [value]
    );

    const params = useMemo(() => {
      let params = null;

      if (excludeExistingValues) {
        params = {
          ...params,
          exclude_record_id: entityId,
          exclude_field_id: field.id,
        };
      }

      const { selectionScope } = field.relation ?? {};

      if (
        selectionScope === SELECTION_SCOPE.SELECTED_RECORDS ||
        selectionScope === SELECTION_SCOPE.EXCLUDED_RECORDS
      ) {
        params = {
          ...params,
          filter_activity_field_id: field.id,
        };
      }

      return params;
    }, [field.relation, field.id, entityId, excludeExistingValues]);

    const [typeaheadProps, typeaheadState, { refetch }] =
      useSelectTypeaheadWithScroll({
        fetch: relationship.search,
        objectToOption: relationship.toOption,
        alwaysOpen,
        onFocus,
        fieldId: fieldId || field.id,
        onMenuOpen,
        onMenuClose,
        selectRef,
        chosenValueIds,
        keepPreviousData,
        params,
        shouldRefetchOnMount,
        relatedObjectId: field.relation?.relatedObject,
      });

    const { setMenuIsOpen, ...customSelectMenuProps } =
      useCustomSelectMenuProps(selectRef, {
        ...others,
        ...typeaheadProps,
      });

    const onStartCreate = () => {
      if (
        selectRef.current.state.menuIsOpen !== false &&
        others.menuIsOpen !== false
      ) {
        if (typeaheadProps.inputValue) {
          parseTypeAhead(typeaheadProps.inputValue, isContact);
        }
        typeaheadProps.onInputChange('');
        if (executeOverride) {
          executeOverride(entityCreatedHandler);
        } else {
          setShowEntityCreateModal(true);
        }
      }
    };

    const selectEnterProps = useSelectEnter({
      handlePressEnter: onStartCreate,
      options: typeaheadProps.options,
      loadingOptions: typeaheadState.loading,
    });

    const spreadProps = {
      ...selectEnterProps,
      ...typeaheadProps,
      ...others,
      ...customSelectMenuProps,
    };

    const { onBlur } = spreadProps;

    const entityCreatedHandler = useCallback(
      async (newEntity) => {
        await refetch();
        // need to invalidate all the fields that are related to this object
        await queryClient.invalidateQueries({
          queryKey: RELATION_FIELD.RELATED_OBJECTS(
            field.relation.relatedObject
          ),
        });

        const option = {
          value: newEntity.id,
          label:
            field?.relation?.fetchUrl === 'client'
              ? newEntity.display_name || getUserLabel(newEntity, t)
              : newEntity.display_name,
        };

        handleChange(
          // displayValue could be a null, so we need to be sure we concat only to array
          (displayValue || []).concat(option)
        );
        setInitialValues({});
        onBlur?.();
      },
      [
        t,
        refetch,
        queryClient,
        field,
        handleChange,
        displayValue,
        setInitialValues,
        onBlur,
      ]
    );

    const ObjectLink = useMemo(
      () =>
        relationship.link
          ? getSelectedValueLink({
              genLink: relationship.link,
            })
          : null,
      [relationship.link]
    );

    const [
      isConfirmModalOpen,
      { showModal: showConfirmationModal, hideModal: hideConfirmationModal },
    ] = useModalControl();

    const selectEntity = useRef(null);

    const onConfirmConfirmation = useCallback(() => {
      handleChange(value);
      hideConfirmationModal();
      onBlur?.();
    }, [value, hideConfirmationModal, handleChange, onBlur]);

    const onHideConfirmation = useCallback(() => {
      const filteredValue = displayValue.filter(
        (v) => v.value !== selectEntity.current
      );
      handleChange(filteredValue);
      hideConfirmationModal();
      onBlur?.();
    }, [displayValue, handleChange, hideConfirmationModal, onBlur]);

    const checkPrimaryField = useCallback(
      async (option, nextValue) => {
        if (
          field.fieldType === FIELD_TYPES.Relationship.type &&
          field.relation?.cardinality === 'one_to_many'
        ) {
          if (option.action === 'remove-value') {
            setConflicts?.((prev) =>
              prev.filter(
                (conflict) => conflict.entityValue?.value !== option.option
              )
            );
          } else if (option.action === 'clear') {
            setConflicts?.([]);
          } else {
            selectEntity.current = option.option?.value;
            const relatedObject = field.relation.relatedObject;
            const relatedField = field.relation.relatedField;

            const entityRecord = await FieldService.getEntityRecordById(
              relatedObject,
              selectEntity.current
            );

            const primaryEntityId =
              entityRecord?.fields[relatedField]?.value.id;

            if (primaryEntityId) {
              showConfirmationModal();
              setConflicts?.((prev) =>
                prev.concat({ entityValue: option.option, primaryEntityId })
              );
              return;
            }
          }
        }
        handleChange(nextValue);
      },
      [
        field.fieldType,
        field.relation,
        showConfirmationModal,
        handleChange,
        setConflicts,
      ]
    );

    const handleOnCheckChange = useCallback(
      (val, option) => {
        if (checkConflictingPrimaryRelationship) {
          checkPrimaryField(option, val);
          setValue(val);
        } else {
          setValue(val);
          handleChange(val);
        }
      },
      [
        checkConflictingPrimaryRelationship,
        handleChange,
        checkPrimaryField,
        setValue,
      ]
    );

    const Modal = field.useFullExperienceForm
      ? CreateEntityModalFull
      : CreateEntityModalShort;

    assignFieldHandle(fieldId || field.id, {
      customTab: (e) => {
        e.preventDefault();
      },
      customFocus: () => {
        selectRef.current?.select?.focus();
        setMenuIsOpen(false);
        return selectRef.current?.select;
      },
      customEscape: () => {
        setMenuIsOpen(false);
      },
      customEnter: (e) => {
        return selectRef.current.state.menuIsOpen;
      },
      customUp: () => {
        setMenuIsOpen(true);
      },
      customDown: () => {
        setMenuIsOpen(true);
      },
      disabled,
    });

    return (
      <>
        <MultiSelect
          ref={selectRef}
          className={className}
          disabled={disabled}
          label={label}
          value={displayValue}
          onChange={handleOnCheckChange}
          placeholder={`${t('Find')} ${relationship.name.plural}`}
          menuTopButton={
            hasAbilityToAdd &&
            !hideAddButton &&
            field.relation?.selectionScope !==
              SELECTION_SCOPE.SELECTED_RECORDS && (
              <TopMenuButton onClick={onStartCreate}>
                {`${t('Add')} ${relationship.name.single}`}
              </TopMenuButton>
            )
          }
          menuLeftButton={<ClearSelectButton />}
          menuRightButton={<ApplySelectButton />}
          endAdornment={<IconAdornment icon={'search'} />}
          isLoading={typeaheadState?.loading}
          PillComponent={ObjectLink}
          {...spreadProps}
          portal={portal}
          /*
            When a modal opens, it traps focus within the modal. This is normally fine,
            but we want to not fire the onBlur event when the modal is opens, because that's
            not an action that the user took.
          */
          onBlur={
            showEntityCreateModal || isConfirmModalOpen
              ? () => null
              : spreadProps.onBlur
          }
        />
        {showEntityCreateModal ? (
          <Modal
            callRecordsEndpoint
            objectType={objectType}
            objectId={field.relation?.relatedObject}
            show={showEntityCreateModal}
            onCreated={entityCreatedHandler}
            onCancel={() => {
              setShowEntityCreateModal(false);
              selectRef.current?.select?.focus();
              setInitialValues({});
            }}
            initialValues={initialValues}
          />
        ) : null}
        <RelationshipConfirmationModal
          show={isConfirmModalOpen}
          onConfirm={onConfirmConfirmation}
          onHide={onHideConfirmation}
        />
      </>
    );
  }
);

export default RelationshipMany;
